import {
  FilterNameType,
  VaultObjectFilter,
  VaultObjectFilterControllableFactor,
} from "@/types/vault-object-filter-types";
import FilterPreset from "@/utils/filterPreset";
import { produce } from "immer";
import { create } from "zustand";

interface VaultFilterStoreInterface {
  currentFilter: VaultObjectFilter;
  stashedFilter: VaultObjectFilter | null;
  originalFilterPreset: typeof FilterPreset;
  currentFilterPreset: typeof FilterPreset;
  actions: {
    initiateFilterPreset: (filter: VaultObjectFilter) => void;
    changeFilter: (filterName: FilterNameType) => void;
    updateFilterValue: <K extends VaultObjectFilterControllableFactor>(
      key: K,
      value: VaultObjectFilter[K],
    ) => void;
    resetFilterValue: (key: VaultObjectFilterControllableFactor) => void;
    stashCurrentFilter: () => void;
    restoreStashedFilter: () => void;
  };
}

const useVaultFilterStore = create<VaultFilterStoreInterface>((set, get) => ({
  currentFilter: FilterPreset.Default_241217,
  stashedFilter: null,
  originalFilterPreset: FilterPreset,
  currentFilterPreset: FilterPreset,
  actions: {
    initiateFilterPreset: (filter) => {
      const initialFilter = Object.keys(FilterPreset).includes(filter.name)
        ? filter
        : FilterPreset.Default_241217;

      set(() => ({
        currentFilter: initialFilter,
        originalFilterPreset: {
          ...FilterPreset,
          [initialFilter.name]: initialFilter,
        },
        currentFilterPreset: {
          ...FilterPreset,
          [initialFilter.name]: initialFilter,
        },
      }));
    },
    changeFilter: (filterName) => {
      set((state) => ({
        currentFilter: state.currentFilterPreset[filterName],
      }));
    },
    updateFilterValue: (key, value) => {
      set((state) => {
        const updatedFilter = produce(state.currentFilter, (draft) => {
          draft[key] = value;
        });

        return {
          currentFilter: updatedFilter,
          currentFilterPreset: {
            ...state.currentFilterPreset,
            [state.currentFilter.name]: updatedFilter,
          },
        };
      });
    },
    resetFilterValue: (key) => {
      const currentFilter = get().currentFilter;
      const originalFilter =
        get().originalFilterPreset[currentFilter.name as FilterNameType];
      const resetFilter = { ...currentFilter, [key]: originalFilter[key] };

      set((state) => ({
        currentFilter: resetFilter,
        currentFilterPreset: {
          ...state.currentFilterPreset,
          [currentFilter.name]: resetFilter,
        },
      }));
    },
    stashCurrentFilter: () => {
      set((state) => ({
        currentFilter: FilterPreset.Default_241217,
        stashedFilter: state.currentFilter,
      }));
    },
    restoreStashedFilter: () => {
      const stashedFilter = get().stashedFilter;
      if (!stashedFilter) return;
      set(() => ({
        currentFilter: stashedFilter,
        stashedFilter: null,
      }));
    },
  },
}));

export default useVaultFilterStore;
